/**
 * Kitchen mode: the order queue and nothing else.
 *
 * A counter tablet during service needs the queue, whether it is still connected, and the buttons to
 * advance an order. It does not need tabs to a menu editor, a location picker, a day selector, or the
 * signed-in user's email — those are owner tasks, and on a small screen they crowd out the only thing
 * anyone is looking at.
 *
 * Implemented as a class on <body> rather than a separate route, so the queue is the same component with
 * the same polling, alarm and connection status. A second "kitchen page" would be a second thing to keep
 * correct, and the two would drift.
 *
 * PERSISTED, because a tablet that reboots overnight must come back to the queue rather than to a
 * general-purpose console nobody notices is on the wrong screen.
 */

const KITCHEN_KEY = "glowin.orders.kitchen";
export const KITCHEN_CLASS = "kitchen-mode";

export function loadKitchenMode(storage: Pick<Storage, "getItem">): boolean {
  try {
    return storage.getItem(KITCHEN_KEY) === "on";
  } catch {
    // Blocked storage must not stop the queue rendering; it just will not be remembered.
    return false;
  }
}

export function saveKitchenMode(storage: Pick<Storage, "setItem">, on: boolean): void {
  try {
    storage.setItem(KITCHEN_KEY, on ? "on" : "off");
  } catch {
    /* not remembered; this session still works */
  }
}

/** Reflect the mode onto the document so CSS can hide everything else. Idempotent. */
export function applyKitchenClass(body: { classList: DOMTokenList }, on: boolean): void {
  body.classList.toggle(KITCHEN_CLASS, on);
}

/**
 * Ask the browser for real fullscreen, hiding its own chrome too.
 *
 * Best effort on purpose: it needs a user gesture, it is refused in some embedded webviews, and the user
 * can leave it with a swipe at any time. Kitchen mode must therefore work WITHOUT it — the class does
 * the useful part, and this only removes the address bar on top.
 */
export async function requestFullscreen(el: {
  requestFullscreen?: () => Promise<void>;
}): Promise<boolean> {
  try {
    if (!el.requestFullscreen) return false;
    await el.requestFullscreen();
    return true;
  } catch {
    return false;
  }
}

export async function exitFullscreen(doc: {
  fullscreenElement?: unknown;
  exitFullscreen?: () => Promise<void>;
}): Promise<void> {
  try {
    if (doc.fullscreenElement && doc.exitFullscreen) await doc.exitFullscreen();
  } catch {
    /* leaving fullscreen is cosmetic; never let it throw into the UI */
  }
}

/** Minimal shapes for the Screen Wake Lock API, which the DOM lib does not declare everywhere. */
export interface WakeLockSentinelLike {
  released?: boolean;
  release: () => Promise<void>;
}
export interface WakeLockLike {
  request: (type: "screen") => Promise<WakeLockSentinelLike>;
}

/**
 * Keep the screen on while the queue is showing.
 *
 * Without this, kitchen mode does not survive a quiet ten minutes: a tablet dims and locks, and a locked
 * page is FROZEN — polling stops, the alarm cannot fire, and nothing recovers until somebody touches it.
 * That is precisely the situation kitchen mode exists for.
 *
 * Best effort, like fullscreen: unsupported in some browsers, refused without a gesture, and the lock is
 * DROPPED whenever the page is hidden — so the caller must re-acquire on visibilitychange rather than
 * assuming one request lasts a shift.
 */
export async function requestWakeLock(nav: {
  wakeLock?: WakeLockLike;
}): Promise<WakeLockSentinelLike | undefined> {
  try {
    if (!nav.wakeLock) return undefined;
    return await nav.wakeLock.request("screen");
  } catch {
    return undefined;
  }
}

export async function releaseWakeLock(lock: WakeLockSentinelLike | undefined): Promise<void> {
  try {
    if (lock && !lock.released) await lock.release();
  } catch {
    /* releasing is cosmetic; never throw into the UI */
  }
}
